import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import SignInForm from "../../components/Auth/SignInForm";
import { Bell } from "lucide-react";
import profile from "../../assets/images/USER.png";
import settings from "../../assets/images/setting.png";
import { useNavigate, useLocation } from "react-router-dom";
import { decryptData } from "../../utils/encryption";
import logoDesktop from "../../assets/images/header-logo.png";
import logoMobile from "../../assets/images/header-logo-mobile.png";
import logoutIcon from "../../assets/images/logout.png";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

const Header = ({ onAboutClick, onContactClick }) => {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [expandAccount, setExpandAccount] = useState(false);
  const [expandSettings, setExpandSettings] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [residentName, setResidentName] = useState("User");
  const { isAuthenticated, logout } = useContext(AuthContext);
  const modalRef = useRef(null);
  const desktopMenuRef = useRef(null); // Ref for desktop menu
  const mobileMenuRef = useRef(null); // Ref for mobile menu
  const desktopHamburgerRef = useRef(null); // Ref for desktop hamburger
  const mobileHamburgerRef = useRef(null); // Ref for mobile hamburger
  const navigate = useNavigate();
  const { user, user_type } = useContext(AuthContext);
  const [clickOnLogo, setClickOnLogo] = useState(false);
  const location = useLocation();
  const [decryptedUser, setDecryptedUser] = useState(null);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);

  const tabMapping = {
    "Manage Your Account": "/resident-parcel-dashboard/manage-account",
    "Pending & Approvals": "Approval",
    "Unlock Your Locker": "Stored",
    "Add Parcels": "Upcoming",
    "Add Members": "Members",
    "Share Invite": "Members",
  };

  const tabSetting = {
    "How to Use?": "/resident-parcel-dashboard/howtouse",
    FAQ: "/resident-parcel-dashboard/faq",
    Support: "/resident-parcel-dashboard/support",
    // "Terms & Conditions": "/resident-parcel-dashboard/TermsAndConditions",
    // "Privacy Policy": "/resident-parcel-dashboard/PrivacyPolicy",
    Feedback: "/resident-parcel-dashboard/feedback",
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowMenu(false);
        setExpandAccount(false);
        setExpandSettings(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("encryptedUser");

    if (stored) {
      const decrypted = decryptData(stored);

      setDecryptedUser(decrypted);
      setIsProfileCompleted(decrypted?.profile_completed === true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (decryptedUser) {
      const userName = decryptedUser?.name || "User";
      setResidentName(userName);
      const hour = new Date().getHours();
      setGreeting(
        hour < 12
          ? "Good Morning 🌤️"
          : hour < 18
            ? "Good Afternoon ☀️"
            : "Good Evening 🌙",
      );
    }
  }, [decryptedUser]);

  useLockBodyScroll(showSignInModal);

  const toggleAccount = () => {
    setExpandAccount((prev) => !prev);
    setExpandSettings(false);
  };

  const toggleSettings = () => {
    setExpandSettings((prev) => !prev);
    setExpandAccount(false);
  };

  const handleAccountOptionClick = (text) => {
    const mapped = tabMapping[text];
    if (mapped?.startsWith("/")) {
      navigate(mapped);
    } else if (mapped) {
      navigate("/resident-parcel-dashboard", {
        state: { activeTab: mapped },
      });
    }
    setShowMenu(false);
  };

  const handleSettingsOptionClick = (text) => {
    const mapped = tabSetting[text];
    if (mapped?.startsWith("/")) {
      navigate(mapped);
    }
    setShowMenu(false);
  };

  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      const clickedInsideDesktopMenu = desktopMenuRef.current?.contains(target);
      const clickedInsideMobileMenu = mobileMenuRef.current?.contains(target);
      const clickedInsideDesktopHamburger =
        desktopHamburgerRef.current?.contains(target);
      const clickedInsideMobileHamburger =
        mobileHamburgerRef.current?.contains(target);

      if (
        !clickedInsideDesktopMenu &&
        !clickedInsideMobileMenu &&
        !clickedInsideDesktopHamburger &&
        !clickedInsideMobileHamburger
      ) {
        setShowMenu(false);
        setExpandAccount(false);
        setExpandSettings(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowMenu(false);
        setExpandAccount(false);
        setExpandSettings(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showMenu]);

  const handleTmwAppClick = () => {
    setClickOnLogo((prev) => !prev);
    if (user_type === "resident") {
      if (decryptedUser) {
        navigate("/resident-parcel-dashboard", {
          state: { decryptedUser },
          replace: true,
        });
      } else {
        console.warn("Missing resident data in localStorage");
      }
    } else if (user_type === "delivery") {
      if (decryptedUser) {
        navigate("/delivery-dashboard", {
          state: { decryptedUser },
          replace: true,
        });
      } else {
        console.warn("Missing resident data in localStorage");
      }
    }
  };

  const AuthMenu = ({ user }) => {
    const userInitial = user?.name?.charAt(0).toUpperCase() || "U";

    return (
      <>
        <div
          className="flex justify-between items-center px-4 py-4 cursor-pointer border-b bg-white"
          onClick={toggleAccount}
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold">
              {userInitial}
            </div>
            <span className="text-sm font-semibold text-blue-700">
              Your Account
            </span>
          </div>
          <span className="text-sm text-blue-700">
            {expandAccount ? "▲" : "▼"}
          </span>
        </div>
        {expandAccount &&
          [
            "Manage Your Account",
            "Pending & Approvals",
            "Unlock Your Locker",
            "Add Parcels",
            "Add Members",
            "Share Invite",
          ].map((text, i) => (
            <div
              key={i}
              className="px-4 py-3 border-b hover:bg-gray-100 cursor-pointer"
              onClick={() => handleAccountOptionClick(text)}
            >
              {text}
            </div>
          ))}
        <div
          className="flex justify-between items-center px-4 py-4 cursor-pointer border-b bg-white"
          onClick={toggleSettings}
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
              <img src={settings} alt="Settings" className="w-7 h-7" />
            </div>
            <span className="text-sm font-semibold text-blue-700">
              Settings
            </span>
          </div>
          <span className="text-sm text-blue-700">
            {expandSettings ? "▲" : "▼"}
          </span>
        </div>
        {expandSettings &&
          [
            "How to Use?",
            "FAQ",
            "Support",
            // "Terms & Conditions",
            // "Privacy Policy",
            "Feedback",
          ].map((text, i) => (
            <div
              key={i}
              className="px-4 py-3 border-b hover:bg-gray-100 cursor-pointer"
              onClick={() => handleSettingsOptionClick(text)}
            >
              {text}
            </div>
          ))}
        <div
          className="flex items-center px-4 py-4 cursor-pointer hover:bg-gray-100 text-black-600 font-semibold"
          onClick={logout}
        >
          <img
            src={logoutIcon}
            alt="Logout"
            className="w-7 h-7 mr-3 object-contain"
          />
          Log Out
        </div>
      </>
    );
  };

  return (
    <div className="sticky top-0 z-50">
      <header className="w-full bg-[#004990] px-4 py-3 relative z-50">
        <div className="max-w-[1350px] mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="flex justify-start w-full md:w-auto">
            <img
              src={logoDesktop}
              alt="Company Logo"
              className="hidden md:block h-[38px] w-auto cursor-pointer"
              onClick={() => {
                if (isAuthenticated) navigate("/");
                setClickOnLogo(true);
              }}
            />
            <img
              src={logoMobile}
              alt="Company Logo"
              className="block md:hidden h-[38px] w-auto cursor-pointer"
              onClick={() => {
                if (isAuthenticated) navigate("/");
                setClickOnLogo(true);
              }}
            />
          </div>

          <div
            className={`hidden md:flex items-center space-x-6 ${
              !isAuthenticated || !isProfileCompleted
                ? "justify-center w-full"
                : "relative"
            }`}
          >
            {!isAuthenticated || !isProfileCompleted ? (
              <div className="hidden md:flex items-center w-full relative px-6">
                <div className="flex space-x-6 justify-center w-full">
                  <button
                    onClick={onAboutClick}
                    className="text-white font-medium hover:text-gray-300"
                  >
                    About
                  </button>
                  <button
                    onClick={onContactClick}
                    className="text-white font-medium hover:text-gray-300"
                  >
                    Contact
                  </button>
                </div>
                <button
                  onClick={() => setShowSignInModal(true)}
                  className="px-9 py-2 bg-white text-[#004990] rounded-full font-medium hover:bg-gray-100 transition-colors duration-300 ml-auto whitespace-nowrap"
                >
                  Sign In / Up
                </button>
              </div>
            ) : location.pathname === "/" && isProfileCompleted ? (
              <button
                onClick={handleTmwAppClick}
                className="px-6 py-2 bg-white text-[#004990] rounded-full font-medium hover:bg-gray-100"
              >
                TMW App
              </button>
            ) : (
              <>
                <div className="relative">
                  {/* Notifications (commented out) */}
                  {/* <Bell size={20} className="text-white cursor-pointer" onClick={() => navigate("/resident-parcel-dashboard/notification")} /> */}
                </div>
                {isAuthenticated && isProfileCompleted && (
                  <button
                    ref={desktopHamburgerRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu((prev) => !prev);
                    }}
                    className="text-white"
                  >
                    <svg
                      className="h-6 w-6"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      fill="none"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
            {showMenu && (
              <div
                ref={desktopMenuRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-full right-0 mt-2 bg-white w-[260px] rounded-xl shadow-xl z-50 py-4"
              >
                <AuthMenu user={decryptedUser} />
              </div>
            )}
          </div>

          <div className="md:hidden absolute top-4 right-4 flex items-center space-x-4">
            {(!isAuthenticated || !isProfileCompleted) && (
              <button
                ref={mobileHamburgerRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu((prev) => !prev);
                }}
                className="text-white"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  fill="none"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}
            {location.pathname === "/" &&
              isAuthenticated &&
              isProfileCompleted &&
              !clickOnLogo && (
                <button
                  onClick={handleTmwAppClick}
                  className="px-3 py-1.5 bg-white text-[#004990] rounded-full text-sm font-semibold hover:bg-gray-100"
                >
                  TMW App
                </button>
              )}
            {isAuthenticated &&
              isProfileCompleted &&
              (clickOnLogo || location.pathname !== "/") && (
                <>
                  <div className="relative">
                    {/* Notifications (commented out) */}
                    {/* <Bell size={20} className="text-white cursor-pointer" onClick={() => navigate("/resident-parcel-dashboard/notification")} /> */}
                  </div>
                  <button
                    ref={mobileHamburgerRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu((prev) => !prev);
                    }}
                    className="text-white"
                  >
                    <svg
                      className="h-6 w-6"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      fill="none"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </button>
                </>
              )}
            {showMenu && (
              <div
                ref={mobileMenuRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-12 right-0 bg-white w-[260px] rounded-xl shadow-xl z-50 py-4"
              >
                {!isAuthenticated || !isProfileCompleted ? (
                  <>
                    <div className="relative">
                      <button
                        onClick={() => {
                          setShowSignInModal(true);
                          setShowMenu(false);
                        }}
                        className="w-[70%] ml-4 bg-[#004990] text-white rounded-full py-2 px-4 text-sm font-semibold mb-4"
                      >
                        Sign In / Up
                      </button>
                      <button
                        onClick={() => setShowMenu(false)}
                        className="absolute top-3 right-4 text-red-500 text-xl font-bold"
                      >
                        ✕
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        onAboutClick();
                        setShowMenu(false);
                      }}
                      className="block px-4 py-3 text-[16px] font-bold text-black hover:text-[#004990]"
                    >
                      ABOUT
                    </button>
                    <button
                      onClick={() => {
                        onContactClick();
                        setShowMenu(false);
                      }}
                      className="block px-4 py-3 text-[16px] font-bold text-black hover:text-[#004990]"
                    >
                      CONTACT
                    </button>
                  </>
                ) : (
                  <AuthMenu user={decryptedUser} />
                )}
              </div>
            )}
          </div>
        </div>

        {showSignInModal && (
          // Redirect from tmw to storage on button code)
          // window.location.href = "https://themailwhale.in/storage/login";
          <div
            id="modal-backdrop"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]"
            onClick={(e) => {
              if (e.target.id === "modal-backdrop") setShowSignInModal(false);
            }}
          >
            <div
              ref={modalRef}
              className="relative bg-white w-[90%] max-w-sm rounded-[14px] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSignInModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
              <SignInForm onClose={() => setShowSignInModal(false)} />
            </div>
          </div>
        )}
      </header>
    </div>
  );
};

export default Header;
